import { graphqlRequest, graphqlUpload } from "./client";
import type { BoardTrack } from "./types";

/* Step 6: the Gig Board. Listings come from the server's job sources; applications, wins and check-ins are saved there. */

export type GigView =
  "rec" | "mine" | "transferable" | "all" | "uiux" | "graphic" | "video" | "digital";
export type GigKind = "any" | "gig" | "job";
export type GigSort = "mixed" | "newest" | "fewest";
export type ApplicationStatus = "clicked" | "none" | "waiting" | "interview" | "won" | "lost";
export type WinPaid = "yes" | "part" | "notyet";

export interface GigListing {
  listingKey: string;
  source: string;
  site: string;
  track: BoardTrack;
  title: string;
  company: string;
  url: string;
  loc: string;
  kind: string;
  pay: string;
  posted: string;
  desc: string;
  bids?: number | null;
  english: boolean;
  direct: boolean;
}

export interface GigApplication {
  id: string;
  listingKey: string;
  title: string;
  company: string;
  source: string;
  url: string;
  status: ApplicationStatus;
  statusAt?: string | null;
  clickedAt: string;
  lastClick: string;
}

export interface GigWin {
  id: string;
  applicationId?: string | null;
  what: string;
  type: string;
  via: string;
  startDate?: string | null;
  paid: WinPaid;
  amount?: number | null;
  currency: string;
  proofName?: string | null;
  hasProof: boolean;
  isPaid: boolean;
  createdAt: string;
}

export interface GigBoardCounts {
  all: number;
  mine: number;
  transferable: number;
  fresh: number;
  freshTransferable: number;
  freshAll: number;
}

export interface GigBoardPage {
  total: number;
  page: number;
  size: number;
  tiles: { listing: GigListing; application?: GigApplication | null }[];
  sources: string[];
  updatedAt?: string | null;
  counts: GigBoardCounts;
}

export interface GigBoardHome {
  open: boolean;
  applications: GigApplication[];
  wins: GigWin[];
  appliedThisWeek: number;
  paidWins: number;
  checkinDue: boolean;
  checkinApplications: GigApplication[];
  lastCheckinAt?: string | null;
}

export interface GigBoardFilter {
  view: GigView;
  kind: GigKind;
  source?: string | null;
  sort: GigSort;
  englishOnly: boolean;
  search?: string;
  page: number;
  size?: number;
}

export interface WinInput {
  what: string;
  type: string;
  via: string;
  startDate?: string | null;
  paid: WinPaid;
  amount?: number | null;
  currency: string;
  applicationId?: string | null;
}

/** Same lists the server validates against (api/src/app/gigboard/entities/GigWin.ts). */
export const WORK_TYPES = [
  "Gig on a platform",
  "Other freelance project",
  "Full-time job",
  "Part-time job",
  "Internship",
  "Other paid work",
];
export const FOUND_VIA = [
  "The Gig Board",
  "A gig platform I joined",
  "A friend or referral",
  "Social media",
  "A past client",
  "Other",
];
export const CURRENCIES = ["NGN", "USD", "Other"];

/** The answers a participant can give about an application (the Friday check-in and My applications). */
export const APP_STATUS: [Exclude<ApplicationStatus, "clicked">, string][] = [
  ["none", "Didn't apply"],
  ["waiting", "Applied, waiting"],
  ["interview", "Interview or reply"],
  ["won", "Got it"],
  ["lost", "Didn't get it"],
];

/** A listing with this many bids or more is flagged "High competition". */
export const HIGH_COMPETITION = 50;
export const GIG_PAGE_SIZE = 12;
/** Largest proof PDF the server keeps; photos and screenshots are shrunk instead. */
export const WIN_PROOF_MAX_PDF = 2.5 * 1024 * 1024;

const LISTING_FIELDS =
  "listingKey source site track title company url loc kind pay posted desc bids english direct";
const APPLICATION_FIELDS =
  "id listingKey title company source url status statusAt clickedAt lastClick";
const WIN_FIELDS =
  "id applicationId what type via startDate paid amount currency proofName hasProof isPaid createdAt";
const HOME_FIELDS = `open applications { ${APPLICATION_FIELDS} } wins { ${WIN_FIELDS} } appliedThisWeek paidWins checkinDue checkinApplications { ${APPLICATION_FIELDS} } lastCheckinAt`;

export const isGigKind = (kind: string) => /gig|contract|part|freelanc|hourly/i.test(kind || "");

export async function fetchGigBoard(filter: GigBoardFilter): Promise<GigBoardPage> {
  return (
    await graphqlRequest<{ getGigBoard: GigBoardPage }>(
      `query($filter: GigBoardFilterInput!) { getGigBoard(filter: $filter) {
        total page size sources updatedAt
        counts { all mine transferable fresh freshTransferable freshAll }
        tiles { listing { ${LISTING_FIELDS} } application { ${APPLICATION_FIELDS} } } } }`,
      { variables: { filter: { size: GIG_PAGE_SIZE, ...filter, source: filter.source || null } } },
    )
  ).getGigBoard;
}

export async function fetchGigBoardHome(): Promise<GigBoardHome> {
  return (
    await graphqlRequest<{ getGigBoardHome: GigBoardHome }>(
      `{ getGigBoardHome { ${HOME_FIELDS} } }`,
    )
  ).getGigBoardHome;
}

/** The participant tapped through to apply: saved to My applications (we can't see the application itself). */
export async function logGigApply(listingKey: string): Promise<GigApplication> {
  return (
    await graphqlRequest<{ logGigApply: GigApplication }>(
      `mutation($listingKey: String!) { logGigApply(listingKey: $listingKey) { ${APPLICATION_FIELDS} } }`,
      { variables: { listingKey } },
    )
  ).logGigApply;
}

export async function setApplicationStatus(
  applicationId: string,
  status: ApplicationStatus,
): Promise<GigApplication> {
  return (
    await graphqlRequest<{ setApplicationStatus: GigApplication }>(
      `mutation($applicationId: String!, $status: ApplicationStatus!) {
        setApplicationStatus(applicationId: $applicationId, status: $status) { ${APPLICATION_FIELDS} } }`,
      { variables: { applicationId, status } },
    )
  ).setApplicationStatus;
}

/** Report a Win, with an optional payment screenshot or contract as proof. */
export async function reportWin(input: WinInput, proof?: File | null): Promise<GigWin> {
  const query = `mutation($input: ReportWinInput!, $proof: Upload) { reportWin(input: $input, proof: $proof) { ${WIN_FIELDS} } }`;
  if (proof) {
    return (await graphqlUpload<{ reportWin: GigWin }>(query, { input }, "proof", proof)).reportWin;
  }
  return (await graphqlRequest<{ reportWin: GigWin }>(query, { variables: { input } })).reportWin;
}

export async function submitCheckin(input: {
  answers: { applicationId: string; status: ApplicationStatus }[];
  otherWork: boolean;
  win?: WinInput | null;
}): Promise<GigBoardHome> {
  return (
    await graphqlRequest<{ submitCheckin: GigBoardHome }>(
      `mutation($input: SubmitCheckinInput!) { submitCheckin(input: $input) { ${HOME_FIELDS} } }`,
      { variables: { input } },
    )
  ).submitCheckin;
}

/* ---------- weekly reminders (Web Push) ---------- */

export async function fetchPushKey(): Promise<{ key: string; enabled: boolean }> {
  return (
    await graphqlRequest<{ getPushKey: { key: string; enabled: boolean } }>(
      `{ getPushKey { key enabled } }`,
    )
  ).getPushKey;
}

export async function subscribePush(sub: {
  endpoint: string;
  p256dh: string;
  auth: string;
}): Promise<boolean> {
  return (
    await graphqlRequest<{ subscribePush: boolean }>(
      `mutation($endpoint: String!, $p256dh: String!, $auth: String!) {
        subscribePush(endpoint: $endpoint, p256dh: $p256dh, auth: $auth) }`,
      { variables: sub },
    )
  ).subscribePush;
}

export async function unsubscribePush(endpoint: string): Promise<boolean> {
  return (
    await graphqlRequest<{ unsubscribePush: boolean }>(
      `mutation($endpoint: String!) { unsubscribePush(endpoint: $endpoint) }`,
      { variables: { endpoint } },
    )
  ).unsubscribePush;
}
